import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { format } from '../../utils/datetime';
import {
  Alert, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle, Grid,
  IconButton, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import HistoryIcon from '@mui/icons-material/History';
import TuneIcon from '@mui/icons-material/Tune';
import BoltIcon from '@mui/icons-material/Bolt';

import { fetchRequest as goalsFetch } from '../../redux/actions/discovery/goals';
import {
  fetchRequest as driversFetch,
  clearState as driversClear,
} from '../../redux/actions/discovery/driversNow';
import {
  fetchRequest as scenariosFetch,
  clearState as scenariosClear,
} from '../../redux/actions/discovery/scenarios';
import {
  fetchRequest as runScenarioRequest,
  clearState as runScenarioClear,
} from '../../redux/actions/discovery/runScenario';
import {
  fetchRequest as parseScenarioRequest,
  clearState as parseScenarioClear,
} from '../../redux/actions/discovery/parseScenario';
import {
  fetchRequest as deleteScenarioRequest,
} from '../../redux/actions/discovery/deleteScenario';
import {
  fetchRequest as explainScenarioRequest,
  clearState as explainScenarioClear,
} from '../../redux/actions/discovery/explainScenario';
import {
  fetchRequest as sensitivityRequest,
  clearState as sensitivityClear,
} from '../../redux/actions/discovery/sensitivity';
import {
  fetchRequest as runSensitivityRequest,
  clearState as runSensitivityClear,
} from '../../redux/actions/discovery/runSensitivity';
import {
  fetchRequest as runTimingRequest,
  clearState as runTimingClear,
} from '../../redux/actions/discovery/runTiming';
import {
  fetchRequest as updateGoalRequest,
} from '../../redux/actions/discovery/updateGoal';

import PageBreadcrumbs from '../common/PageBreadcrumbs';
import ScenarioBuilder from './ScenarioBuilder';
import ScenarioFanChart from './ScenarioFanChart';
import ScenarioWaterfall from './ScenarioWaterfall';
import ScenarioTornado from './ScenarioTornado';

const EMPTY_SPEC = { name: '', overrides: [], events: [] };

const fmtTs = (iso) => {
  if (!iso) return '—';
  try { return format(new Date(iso), 'MMM d, HH:mm'); } catch (_e) { return '—'; }
};

const MECHANISM_LABELS = {
  'model-exact': 'model-exact',
  'chain-estimated': 'estimated via validated chain',
  'event-overlay': 'measured event response',
};

const isProbabilityGoal = (goalType) => goalType === 'trend' || goalType === 'anomaly';

// At-a-glance: where does the scenario path end vs the baseline path?
// ``unit`` is the target's measurement unit ("$/MWh"); percentage deltas
// carry the absolute move in that unit alongside.
const endDelta = (doc, probability, unit) => {
  const b = doc?.baseline?.point;
  const f = doc?.fan?.point;
  if (!Array.isArray(b) || !Array.isArray(f) || !b.length || !f.length) return null;
  const base = b[b.length - 1];
  const sc = f[f.length - 1];
  if (base == null || sc == null) return null;
  const diff = sc - base;
  if (probability) {
    const pts = Math.round(diff * 1000) / 10;
    return { up: diff >= 0, text: `${diff >= 0 ? '+' : ''}${pts} pts vs baseline at the horizon` };
  }
  const absMove = `${diff >= 0 ? '+' : ''}${Math.abs(diff) >= 100 ? diff.toFixed(0) : diff.toFixed(2)}${unit ? ` ${unit}` : ''}`;
  if (base !== 0) {
    const pct = (100 * diff) / Math.abs(base);
    return {
      up: diff >= 0,
      text: `${diff >= 0 ? '+' : ''}${pct.toFixed(1)}% (${absMove}) vs baseline at the horizon`,
    };
  }
  return { up: diff >= 0, text: `${absMove} vs baseline at the horizon` };
};

const COMPACT = { p: 1.75, '&:last-child': { pb: 1.75 } };

// "12481.3" → "$12,481" — the unit-economics money readout.
const fmtMoney = (v, currency = '$') => {
  if (v == null || Number.isNaN(v)) return null;
  const sign = v < 0 ? '−' : '+';
  const a = Math.abs(v);
  const num = a >= 100
    ? a.toLocaleString(undefined, { maximumFractionDigits: 0 })
    : a.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return `${sign}${currency}${num}`;
};

// Historical analogue replay: what the target ACTUALLY did the last few
// times this event stream fired — measured evidence next to the simulation.
const EventAnalogues = ({ events }) => {
  const items = (events || []).filter(
    (ev) => (ev.analogues?.analogues || []).length > 0,
  );
  if (!items.length) return null;
  return (
    <Stack spacing={1}>
      {items.map((ev) => {
        const an = ev.analogues;
        const hours = Math.round((an.horizon_s || 0) / 3600);
        const withMove = an.analogues.filter((a) => a.move_pct != null);
        return (
          <Box
            key={ev.stream}
            sx={{
              border: '1px solid', borderColor: 'divider', borderRadius: 1.5,
              px: 1.5, py: 1.25,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <HistoryIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                History check — &lsquo;{ev.stream}&rsquo;
              </Typography>
            </Stack>
            {withMove.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                In the last {withMove.length} real occurrences, the target moved
                between {an.move_min_pct}% and {an.move_max_pct}% within {hours}h
                (measured, not simulated).
              </Typography>
            )}
            <Stack spacing={0.25}>
              {an.analogues.slice(0, 5).map((a) => (
                <Stack key={a.event_id || a.occurred_at} direction="row" spacing={1} alignItems="baseline">
                  <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0, minWidth: 88 }}>
                    {a.occurred_at ? format(new Date(a.occurred_at), 'MMM d, yyyy') : '—'}
                  </Typography>
                  <Typography variant="caption" noWrap sx={{ flexGrow: 1, minWidth: 0 }} color="text.secondary">
                    {a.name || '—'}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      flexShrink: 0,
                      fontWeight: 600,
                      color: a.move_pct == null ? 'text.disabled'
                        : (a.move_pct >= 0 ? 'warning.main' : 'success.main'),
                    }}
                  >
                    {a.move_pct == null ? 'no data'
                      : `${a.move_pct >= 0 ? '+' : ''}${a.move_pct}%`}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
};

const CONFIDENCE_COLOR = { high: 'success', medium: 'info', low: 'warning' };

// The short LLM "why" — headline, provenance-cited reasons, confidence.
const ScenarioReport = ({ report, loading, error, onRetry }) => {
  if (loading) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 0.5 }}>
        <CircularProgress size={13} />
        <Typography variant="caption" color="text.secondary">
          Writing the report…
        </Typography>
      </Stack>
    );
  }
  if (error) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          Report unavailable: {String(error)}
        </Typography>
        {onRetry && <Button size="small" onClick={onRetry}>Retry</Button>}
      </Stack>
    );
  }
  if (!report) return null;
  return (
    <Box sx={{
      border: '1px solid', borderColor: 'divider', borderRadius: 1.5,
      px: 1.5, py: 1.25, bgcolor: 'action.hover',
    }}>
      <Stack spacing={0.75}>
        <Stack direction="row" spacing={1} alignItems="center">
          <AutoAwesomeIcon sx={{ fontSize: 15, color: 'info.main' }} />
          <Typography variant="body2" sx={{ fontWeight: 600, flexGrow: 1 }}>
            {report.headline}
          </Typography>
          {report.confidence && (
            <Chip
              size="small" variant="outlined"
              color={CONFIDENCE_COLOR[report.confidence] || 'default'}
              label={`${report.confidence} confidence`}
              sx={{ height: 20, flexShrink: 0 }}
            />
          )}
        </Stack>
        {(report.reasons || []).map((r) => (
          <Typography key={r} variant="caption" color="text.secondary">
            • {r}
          </Typography>
        ))}
        {report.confidence_note && (
          <Typography variant="caption" color="text.disabled">
            {report.confidence_note}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

const ScenarioResult = ({
  doc, intervalSeconds, probability, unit, report, reportLoading, reportError,
  onRetryReport, unitEconomics, onEditUnitEconomics,
}) => {
  if (!doc) return null;
  const targetUnit = doc.snapshot?.target_unit || unit || null;
  const delta = endDelta(doc, probability, targetUnit);

  // Unit economics: translate the end-of-horizon delta into money when the
  // goal carries a value-per-unit setting (level targets only).
  const b = doc.baseline?.point;
  const f = doc.fan?.point;
  const endDiff = (Array.isArray(b) && Array.isArray(f) && b.length && f.length)
    ? f[f.length - 1] - b[b.length - 1] : null;
  const money = (!probability && unitEconomics?.value_per_unit && endDiff != null)
    ? fmtMoney(endDiff * unitEconomics.value_per_unit, unitEconomics.currency || '$')
    : null;

  const baselineEndVal = Array.isArray(b) && b.length ? b[b.length - 1] : null;

  return (
    <Card variant="outlined">
      <CardContent sx={COMPACT}>
        <Stack spacing={1.25}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle2">{doc.name}</Typography>
            {(doc.mechanisms || []).map((m) => (
              <Chip key={m} size="small" variant="outlined" color="info" label={MECHANISM_LABELS[m] || m} />
            ))}
            <Box sx={{ flexGrow: 1 }} />
            <Typography variant="caption" color="text.secondary">
              {fmtTs(doc.created_at)}
            </Typography>
          </Stack>

          {delta && (
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
              {delta.up
                ? <TrendingUpIcon sx={{ fontSize: 20, color: 'warning.main' }} />
                : <TrendingDownIcon sx={{ fontSize: 20, color: 'success.main' }} />}
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {delta.text}
              </Typography>
              {money && (
                <Chip
                  size="small" color={endDiff >= 0 ? 'warning' : 'success'} variant="outlined"
                  label={`≈ ${money}${unitEconomics.label ? ` ${unitEconomics.label}` : ''}`}
                  onDelete={onEditUnitEconomics}
                  deleteIcon={<EditOutlinedIcon />}
                />
              )}
              {!money && !probability && onEditUnitEconomics && endDiff != null && (
                <Button size="small" sx={{ textTransform: 'none' }} onClick={onEditUnitEconomics}>
                  Value this in {unitEconomics?.currency || '$'}…
                </Button>
              )}
            </Stack>
          )}

          {(doc.warnings || []).map((w) => (
            <Alert key={w} severity="warning" icon={<WarningAmberIcon fontSize="small" />} sx={{ py: 0 }}>
              {w}
            </Alert>
          ))}

          <ScenarioFanChart
            baseline={doc.baseline?.point}
            fan={doc.fan?.point}
            baselineBands={doc.baseline?.bands}
            fanBands={doc.fan?.bands}
            intervalSeconds={intervalSeconds || doc.snapshot?.interval_seconds || 3600}
            probability={probability}
            unit={targetUnit}
          />

          {doc.attribution?.contributions?.length > 0 && baselineEndVal != null && (
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.25 }}>
                What moved the needle
              </Typography>
              <ScenarioWaterfall
                attribution={doc.attribution}
                baselineEnd={baselineEndVal}
                unit={targetUnit}
                probability={probability}
              />
            </Box>
          )}

          <EventAnalogues events={doc.events} />

          <ScenarioReport
            report={report}
            loading={reportLoading}
            error={reportError}
            onRetry={onRetryReport}
          />

          <Stack spacing={0.25}>
            {(doc.caveats || []).map((c) => (
              <Typography key={c} variant="caption" color="text.secondary">• {c}</Typography>
            ))}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

const ScenarioComparison = ({ docs, intervalSeconds, probability, unit, onClear }) => {
  const baseline = docs[0]?.baseline?.point;
  const series = docs
    .filter((d) => d?.fan?.point?.length)
    .map((d) => ({ label: d.name || d.scenario_id, fan: d.fan.point }));
  const targetUnit = docs[0]?.snapshot?.target_unit || unit || null;
  return (
    <Card variant="outlined">
      <CardContent sx={COMPACT}>
        <Stack spacing={1.25}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2">Comparing {docs.length} scenarios</Typography>
            <Button size="small" onClick={onClear}>Clear</Button>
          </Stack>
          <ScenarioFanChart
            baseline={baseline}
            series={series}
            intervalSeconds={intervalSeconds || docs[0]?.snapshot?.interval_seconds || 3600}
            probability={probability}
            unit={targetUnit}
          />
          <Typography variant="caption" color="text.secondary">
            All scenarios share the baseline of {docs[0]?.name || 'the first selection'}.
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Event timing sweep — "if I can pick WHEN this event happens, which timing
// moves the goal most?" One champion inference (the baseline); the engine
// slides the stream's measured response curve across every horizon step
// analytically. Only rendered when the goal has validated event drivers.
// ---------------------------------------------------------------------------

const TimingBars = ({ doc, unit }) => {
  const offsets = doc?.offsets || [];
  if (offsets.length === 0) return null;
  const W = 460; const H = 110; const PAD = 6; const AXIS = 16;
  const innerW = W - PAD * 2;
  const innerH = H - PAD - AXIS;
  const vals = offsets.map((o) => Number(o.end_delta) || 0);
  const lo = Math.min(0, ...vals);
  const hi = Math.max(0, ...vals);
  const span = (hi - lo) || 1;
  const y = (v) => PAD + innerH * (1 - (v - lo) / span);
  const zeroY = y(0);
  const bw = innerW / offsets.length;
  const best = doc.best_end_offset;
  const fmtD = (v) => `${v >= 0 ? '+' : ''}${Math.abs(v) >= 100 ? v.toFixed(0) : v.toFixed(2)}${unit ? ` ${unit}` : ''}`;
  return (
    <Box component="svg" viewBox={`0 0 ${W} ${H}`} sx={{ width: '100%', display: 'block' }}>
      <line x1={PAD} x2={W - PAD} y1={zeroY} y2={zeroY} stroke="currentColor" opacity={0.2} />
      {offsets.map((o, i) => {
        const v = vals[i];
        const bx = PAD + i * bw;
        const by = Math.min(y(v), zeroY);
        const bh = Math.max(Math.abs(y(v) - zeroY), 1);
        const isBest = i === best;
        return (
          <g key={o.offset_bins}>
            <title>
              {`Step ${o.offset_bins} (${new Date(o.at_ms).toLocaleString()}): end ${fmtD(v)} · peak ${fmtD(Number(o.peak_delta) || 0)}`}
            </title>
            <rect
              x={bx + bw * 0.15} y={by} width={bw * 0.7} height={bh} rx={1.5}
              fill={isBest ? '#f59e0b' : 'currentColor'}
              opacity={isBest ? 0.95 : 0.35}
            />
          </g>
        );
      })}
      <text x={PAD} y={H - 4} fontSize={9} fill="currentColor" opacity={0.5}>now</text>
      <text x={W - PAD} y={H - 4} fontSize={9} fill="currentColor" opacity={0.5} textAnchor="end">
        {`step ${offsets[offsets.length - 1].offset_bins}`}
      </text>
    </Box>
  );
};

const EventTimingCard = ({ goalId, eventStreams, unit }) => {
  const dispatch = useDispatch();
  const timingState = useSelector((s) => s.discovery_run_timing);
  const [stream, setStream] = useState('');
  const loading = Boolean(timingState?.isLoading);
  const doc = timingState?.response;

  // Keep the picker valid as drivers load; reset results when the goal changes.
  useEffect(() => {
    setStream((prev) => (eventStreams.includes(prev) ? prev : (eventStreams[0] || '')));
  }, [eventStreams]);
  useEffect(() => () => { dispatch(runTimingClear()); }, [dispatch, goalId]);

  if (eventStreams.length === 0) return null;

  const run = () => {
    if (!stream || loading) return;
    dispatch(runTimingRequest({ goalId, stream }));
  };

  const bestOffset = doc?.offsets?.[doc.best_end_offset];
  return (
    <Card variant="outlined">
      <CardContent sx={COMPACT}>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center">
            <BoltIcon sx={{ fontSize: 17, color: 'warning.main' }} />
            <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
              Event timing
            </Typography>
            <TextField
              select size="small" hiddenLabel value={stream}
              onChange={(e) => setStream(e.target.value)}
              sx={{ minWidth: 150 }}
            >
              {eventStreams.map((s) => (<MenuItem key={s} value={s}>{s}</MenuItem>))}
            </TextField>
            <Button
              size="small" variant="outlined" onClick={run} disabled={loading || !stream}
              startIcon={loading ? <CircularProgress size={12} /> : null}
            >
              {loading ? 'Sweeping…' : (doc ? 'Re-run' : 'Sweep')}
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            If you could pick when a {stream ? `'${stream}'` : ''} event happens,
            which timing moves this goal most? The measured response curve slides
            across every forecast step — one model run, the sweep is analytic.
          </Typography>
          {timingState?.error && (
            <Alert severity="error" variant="outlined" sx={{ py: 0.25 }}
              onClose={() => dispatch(runTimingClear())}>
              {String(timingState.error)}
            </Alert>
          )}
          {doc && doc.stream && (
            <>
              <TimingBars doc={doc} unit={unit} />
              {bestOffset && (
                <Typography variant="caption" color="text.secondary">
                  Biggest end-of-horizon impact when it lands at{' '}
                  <strong>
                    step {bestOffset.offset_bins}
                    {bestOffset.offset_bins === 0 ? ' (now)' : ''}
                  </strong>
                  {' '}({new Date(bestOffset.at_ms).toLocaleString(undefined, {
                    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                  })}): {`${bestOffset.end_delta >= 0 ? '+' : ''}${bestOffset.end_delta}`}
                  {unit ? ` ${unit}` : ''}.
                  {doc.best_peak_offset !== doc.best_end_offset
                    ? ` Sharpest peak: step ${doc.best_peak_offset}.` : ''}
                </Typography>
              )}
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

const ScenariosPage = ({ embedded = false, goalId: goalIdProp }) => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedSaved, setSelectedSaved] = useState(null);
  const [spec, setSpec] = useState(EMPTY_SPEC);
  const [compareIds, setCompareIds] = useState(() => new Set());
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // Driver sensitivity (tornado) — persisted server-side; latest fetched on
  // goal change, a fresh run is an explicit (charged) action. Local
  // `sensitivity` merges the two slice responses (free read + fresh run).
  const [sensitivity, setSensitivity] = useState(null);
  const [sensError, setSensError] = useState(null);

  // Unit economics editor (value of one unit of the target, in money).
  const [ueOpen, setUeOpen] = useState(false);
  const [ueValue, setUeValue] = useState('');
  const [ueCurrency, setUeCurrency] = useState('$');
  const [ueLabel, setUeLabel] = useState('');
  // Marks a PATCH initiated from THIS dialog so a stale
  // discovery_update_goal response (e.g. from GoalSettingsCard) can't
  // close it on mount.
  const ueSaveRef = useRef(false);

  const goals = useSelector((s) => s.discovery_goals?.response) || [];
  const goalsLoading = useSelector((s) => Boolean(s.discovery_goals?.isLoading));
  const drState = useSelector((s) => s.discovery_drivers_now);
  const savedState = useSelector((s) => s.discovery_scenarios);
  const runState = useSelector((s) => s.discovery_run_scenario);
  const parseState = useSelector((s) => s.discovery_parse_scenario);
  const explainState = useSelector((s) => s.discovery_explain_scenario);
  const sensState = useSelector((s) => s.discovery_sensitivity);
  const runSensState = useSelector((s) => s.discovery_run_sensitivity);
  const updateGoalState = useSelector((s) => s.discovery_update_goal);
  const sensLoading = Boolean(runSensState?.isLoading);
  const ueSaving = Boolean(updateGoalState?.isLoading);

  const servable = useMemo(
    () => goals.filter((gl) => gl.champion_pipeline_id),
    [goals],
  );
  const urlGoal = searchParams.get('goal') || '';
  const goalId = embedded
    ? (goalIdProp || '')
    : (urlGoal && goals.some((gl) => gl.goal_id === urlGoal)
      ? urlGoal
      : (servable[0]?.goal_id || ''));
  const goal = goals.find((gl) => gl.goal_id === goalId);
  const probability = isProbabilityGoal(goal?.goal_type);

  useEffect(() => { dispatch(goalsFetch()); }, [dispatch]);

  useEffect(() => {
    if (!goalId) return undefined;
    dispatch(driversFetch({ goalId }));
    dispatch(scenariosFetch({ goalId }));
    setSelectedSaved(null);
    setCompareIds(new Set());
    setConfirmDeleteId(null);
    setSpec(EMPTY_SPEC);
    // Latest persisted sensitivity for this goal (free read; the saga
    // swallows failures — "Not run yet" is a normal state).
    setSensitivity(null);
    setSensError(null);
    dispatch(sensitivityRequest({ goalId }));
    return () => {
      dispatch(driversClear());
      dispatch(scenariosClear());
      dispatch(runScenarioClear());
      dispatch(parseScenarioClear());
      dispatch(explainScenarioClear());
      dispatch(sensitivityClear());
      dispatch(runSensitivityClear());
    };
  }, [dispatch, goalId]);

  // Mirror the two sensitivity slices into local state: the free read
  // returns the persisted doc, a fresh (charged) run returns the new one.
  useEffect(() => {
    if (sensState?.response) setSensitivity(sensState.response);
  }, [sensState?.response]);

  useEffect(() => {
    if (runSensState?.response) setSensitivity(runSensState.response);
  }, [runSensState?.response]);

  useEffect(() => {
    if (runSensState?.error) setSensError(runSensState.error);
  }, [runSensState?.error]);

  // When a natural-language parse lands, prefill the builder and auto-run.
  const lastParseRef = useRef(null);
  useEffect(() => {
    const parsed = parseState?.response;
    if (!parsed || parsed === lastParseRef.current) return;
    lastParseRef.current = parsed;
    if (parsed.spec) {
      setSpec(parsed.spec);
      setSelectedSaved(null);
      setCompareIds(new Set());
      dispatch(runScenarioRequest({ goalId, spec: parsed.spec }));
    }
  }, [parseState?.response, dispatch, goalId]);

  const latestRun = runState?.response;
  const saved = useMemo(() => savedState?.response || [], [savedState?.response]);
  const shown = selectedSaved || latestRun;

  // Auto-fetch the short "why" report for the shown scenario. Reports are
  // cached server-side on the scenario doc, so a saved scenario that was
  // explained before comes back instantly and free. Failed attempts are
  // not auto-retried (the user gets a manual Retry button instead).
  const shownId = shown?.scenario_id;
  const shownReport = (shownId && explainState?.reports?.[shownId]) || shown?.report;
  const reportAttemptedRef = useRef(new Set());
  useEffect(() => {
    if (!goalId || !shownId || shownReport) return;
    if (explainState?.loadingId === shownId) return;
    if (reportAttemptedRef.current.has(shownId)) return;
    reportAttemptedRef.current.add(shownId);
    dispatch(explainScenarioRequest({ goalId, scenarioId: shownId }));
  }, [dispatch, goalId, shownId, shownReport, explainState?.loadingId]);
  const retryReport = () => {
    if (!goalId || !shownId) return;
    dispatch(explainScenarioRequest({ goalId, scenarioId: shownId }));
  };

  const compareDocs = useMemo(
    () => saved.filter((sc) => compareIds.has(sc.scenario_id)),
    [saved, compareIds],
  );
  const comparing = compareDocs.length >= 2;

  const toggleCompare = (id) => {
    setCompareIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const runScenario = (builtSpec) => {
    setSelectedSaved(null);
    setCompareIds(new Set());
    dispatch(runScenarioRequest({ goalId, spec: builtSpec }));
  };

  const parse = (text) => dispatch(parseScenarioRequest({ goalId, text }));

  const runSensitivity = () => {
    if (!goalId || sensLoading) return;
    setSensError(null);
    dispatch(runSensitivityRequest({ goalId }));
  };

  const unitEconomics = goal?.options?.unit_economics || null;
  const openUnitEconomics = () => {
    setUeValue(unitEconomics?.value_per_unit != null
      ? String(unitEconomics.value_per_unit) : '');
    setUeCurrency(unitEconomics?.currency || '$');
    setUeLabel(unitEconomics?.label || '');
    setUeOpen(true);
  };
  const saveUnitEconomics = () => {
    const num = parseFloat(ueValue);
    const body = (ueValue === '' || Number.isNaN(num))
      ? { unit_economics: null }
      : {
        unit_economics: {
          value_per_unit: num,
          currency: ueCurrency || '$',
          label: ueLabel || '',
        },
      };
    // Reuses the discovery_update_goal slice (PATCH /goals/:goalId).
    ueSaveRef.current = true;
    dispatch(updateGoalRequest({ goalId, fields: body }));
  };

  useEffect(() => {
    if (!ueSaveRef.current || !updateGoalState?.response) return;
    ueSaveRef.current = false;
    setUeOpen(false);
    dispatch(goalsFetch()); // refresh goal.options
  }, [updateGoalState?.response, dispatch]);

  useEffect(() => {
    // The dialog stays open on failure, mirroring the old swallowed
    // catch; the saga's global Error handler surfaces the message.
    if (ueSaveRef.current && updateGoalState?.error) ueSaveRef.current = false;
  }, [updateGoalState?.error]);

  const confirmDelete = (id) => {
    dispatch(deleteScenarioRequest({ goalId, scenarioId: id }));
    setConfirmDeleteId(null);
    setCompareIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    if (selectedSaved?.scenario_id === id) setSelectedSaved(null);
  };

  const driversLoading = Boolean(drState?.isLoading);
  const driverList = drState?.response?.drivers;
  const predictiveList = drState?.response?.predictive_drivers;
  // Validated event streams — the timing sweep needs a measured response
  // curve, which only edged streams have.
  const eventStreamKeys = useMemo(
    () => [...new Set((driverList || [])
      .filter((d) => d.kind === 'event' && d.key)
      .map((d) => d.key))],
    [driverList],
  );
  // The goal target's measurement unit — from drivers-now (live catalog);
  // stored scenarios also snapshot it as ``snapshot.target_unit``.
  const targetUnit = drState?.response?.target_unit || null;
  const noDrivers = !driversLoading
    && (driverList || []).filter((d) => d.simulatable !== false).length === 0
    && (predictiveList || []).filter((d) => d.simulatable !== false).length === 0;
  const noChampion = Boolean(goal) && !goal.champion_pipeline_id;
  const parsed = parseState?.response;

  return (
    <Box sx={{ width: '100%' }}>
      {!embedded && (
        <PageBreadcrumbs items={[{ text: 'Scenarios', icon: <AltRouteIcon /> }]} mb={1.5} />
      )}

      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
        {!embedded && (
          <TextField
            select size="small" label="Goal" value={goalId || ''}
            onChange={(e) => setSearchParams({ goal: e.target.value })}
            sx={{ minWidth: { xs: '100%', sm: 280 } }}
            disabled={goalsLoading || servable.length === 0}
          >
            {servable.map((gl) => (
              <MenuItem key={gl.goal_id} value={gl.goal_id}>
                {gl.name || gl.target_key}
              </MenuItem>
            ))}
          </TextField>
        )}
        {goal?.goal_type && <Chip size="small" variant="outlined" label={goal.goal_type} />}
      </Stack>

      {goalsLoading && <CircularProgress size={22} />}

      {!embedded && !goalsLoading && servable.length === 0 && (
        <Alert severity="info" variant="outlined" sx={{ maxWidth: 640, py: 0.25 }}>
          Run discovery on a goal first — simulations use its live forecast model.
        </Alert>
      )}

      {goalId && noChampion && (
        <Alert severity="info" variant="outlined" sx={{ maxWidth: 640, py: 0.25 }}>
          No forecast model is live for this goal yet — run discovery, then come back to explore what-ifs.
        </Alert>
      )}

      {goalId && !noChampion && (
        <Grid container spacing={2}>
          <Grid item xs={12} lg={5}>
            <Stack spacing={1.25}>
              <ScenarioBuilder
                drivers={driverList}
                predictiveDrivers={predictiveList}
                scenarioExamples={drState?.response?.scenario_examples}
                value={spec}
                onChange={setSpec}
                onRun={runScenario}
                isRunning={Boolean(runState?.isLoading)}
                onParse={parse}
                isParsing={Boolean(parseState?.isLoading)}
              />

              {noDrivers && (
                <Typography variant="caption" color="text.secondary" sx={{ px: 0.5 }}>
                  No validated drivers yet — you can still describe a what-if, but mappings may be limited.
                </Typography>
              )}

              {parseState?.error && (
                <Alert severity="error" variant="outlined" sx={{ py: 0.25 }}
                  onClose={() => dispatch(parseScenarioClear())}>
                  {String(parseState.error)}
                </Alert>
              )}
              {parsed && (parsed.unmapped || []).length > 0 && (
                <Typography variant="caption" color="warning.main" sx={{ px: 0.5 }}>
                  Couldn&apos;t map: {parsed.unmapped.join(', ')}
                </Typography>
              )}

              {saved.length > 0 && (
                <Card variant="outlined">
                  <CardContent sx={COMPACT}>
                    <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 0.5 }}>
                      <Typography variant="subtitle2">Saved ({saved.length})</Typography>
                      <Typography variant="caption" color="text.secondary">tick 2+ to compare</Typography>
                    </Stack>
                    <Stack spacing={0.25}>
                      {saved.map((sc) => {
                        const active = shown?.scenario_id === sc.scenario_id;
                        return (
                          <Stack
                            key={sc.scenario_id} direction="row" spacing={1} alignItems="center"
                            sx={{
                              borderRadius: 1, px: 0.5, py: 0.25,
                              bgcolor: active ? 'action.selected' : 'transparent',
                              '&:hover': { bgcolor: active ? 'action.selected' : 'action.hover' },
                            }}
                          >
                            <Checkbox
                              size="small" checked={compareIds.has(sc.scenario_id)}
                              onChange={() => toggleCompare(sc.scenario_id)} sx={{ p: 0.25 }}
                            />
                            <Box onClick={() => setSelectedSaved(sc)} sx={{ flexGrow: 1, minWidth: 0, cursor: 'pointer' }}>
                              <Typography variant="body2" noWrap>{sc.name}</Typography>
                            </Box>
                            {confirmDeleteId === sc.scenario_id ? (
                              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                                <Button size="small" color="error" onClick={() => confirmDelete(sc.scenario_id)}>Delete</Button>
                                <Button size="small" color="inherit" onClick={() => setConfirmDeleteId(null)}>Cancel</Button>
                              </Stack>
                            ) : (
                              <>
                                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                                  {fmtTs(sc.created_at)}
                                </Typography>
                                <IconButton size="small" aria-label="Delete scenario"
                                  onClick={() => setConfirmDeleteId(sc.scenario_id)} sx={{ flexShrink: 0 }}>
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </>
                            )}
                          </Stack>
                        );
                      })}
                    </Stack>
                  </CardContent>
                </Card>
              )}
            </Stack>
          </Grid>

          <Grid item xs={12} lg={7}>
            <Stack spacing={1.25}>
              {runState?.isLoading && (
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ p: 1.5 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">Running the simulation…</Typography>
                </Stack>
              )}
              {runState?.error && (
                <Alert severity="error" variant="outlined" sx={{ py: 0.25 }}>{String(runState.error)}</Alert>
              )}
              {comparing ? (
                <ScenarioComparison
                  docs={compareDocs}
                  intervalSeconds={goal?.interval_seconds}
                  probability={probability}
                  unit={targetUnit}
                  onClear={() => setCompareIds(new Set())}
                />
              ) : shown ? (
                <ScenarioResult
                  doc={shown}
                  intervalSeconds={goal?.interval_seconds}
                  probability={probability}
                  unit={targetUnit}
                  report={shownReport}
                  reportLoading={explainState?.loadingId === shownId}
                  reportError={!shownReport && reportAttemptedRef.current.has(shownId)
                    && explainState?.loadingId !== shownId ? explainState?.error : null}
                  onRetryReport={retryReport}
                  unitEconomics={unitEconomics}
                  onEditUnitEconomics={openUnitEconomics}
                />
              ) : !runState?.isLoading && (
                <Box sx={{
                  border: '1px dashed', borderColor: 'divider', borderRadius: 2,
                  py: 6, px: 3, textAlign: 'center',
                }}>
                  <AltRouteIcon sx={{ fontSize: 30, color: 'text.disabled', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Your simulation will appear here
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    Describe a what-if, or build one on the left.
                  </Typography>
                </Box>
              )}

              <Card variant="outlined">
                <CardContent sx={COMPACT}>
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TuneIcon sx={{ fontSize: 17, color: 'text.secondary' }} />
                      <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                        Driver sensitivity
                      </Typography>
                      {sensitivity?.created_at && (
                        <Typography variant="caption" color="text.secondary">
                          {fmtTs(sensitivity.created_at)}
                        </Typography>
                      )}
                      <Button
                        size="small" variant="outlined" onClick={runSensitivity}
                        disabled={sensLoading}
                        startIcon={sensLoading ? <CircularProgress size={12} /> : null}
                      >
                        {sensLoading ? 'Probing…' : (sensitivity ? 'Re-run' : 'Run')}
                      </Button>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      Every simulatable driver probed with a ±{sensitivity?.magnitude_pct || 10}% step
                      through the live forecast model — which lever moves this goal the most.
                    </Typography>
                    {sensError && (
                      <Alert severity="error" variant="outlined" sx={{ py: 0.25 }}
                        onClose={() => setSensError(null)}>
                        {String(sensError)}
                      </Alert>
                    )}
                    {(sensitivity?.warnings || []).map((w) => (
                      <Alert key={w} severity="warning" icon={<WarningAmberIcon fontSize="small" />} sx={{ py: 0 }}>
                        {w}
                      </Alert>
                    ))}
                    {sensitivity ? (
                      <ScenarioTornado sensitivity={sensitivity} probability={probability} />
                    ) : !sensLoading && (
                      <Typography variant="caption" color="text.disabled">
                        Not run yet for this goal.
                      </Typography>
                    )}
                  </Stack>
                </CardContent>
              </Card>

              <EventTimingCard
                goalId={goalId}
                eventStreams={eventStreamKeys}
                unit={targetUnit}
              />
            </Stack>
          </Grid>
        </Grid>
      )}

      <Dialog open={ueOpen} onClose={() => setUeOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Value of one unit</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              How much is one unit of this goal&apos;s target worth? Scenario deltas
              will be translated into money (e.g. −1,240 {targetUnit || 'units'} ≈ −$86,800).
              Leave the value empty to remove.
            </Typography>
            <Stack direction="row" spacing={1}>
              <TextField
                size="small" label="Currency" value={ueCurrency}
                onChange={(e) => setUeCurrency(e.target.value)} sx={{ width: 90 }}
              />
              <TextField
                size="small" label={`Value per ${targetUnit || 'unit'}`} value={ueValue}
                onChange={(e) => setUeValue(e.target.value)}
                type="number" autoFocus fullWidth
              />
            </Stack>
            <TextField
              size="small" label="Label (optional)" value={ueLabel}
              onChange={(e) => setUeLabel(e.target.value)}
              placeholder="e.g. gross margin impact" fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUeOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveUnitEconomics} disabled={ueSaving}>
            {ueSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScenariosPage;
